// İSG uzmanı portalı — Güvenlik masası günlük geçiş listesi (`GET /security/entries`,
// api-contract §29.5, S32b). "Bugün" paneli TEK istek atar (`result: 'all'`) — hem tahliye
// yoklamasını (`result==='Allowed'` satırları) HEM reddedilen denemeleri AYNI yanıttan türetir;
// panelin "Tümü"/"Girenler" süzgeci bu yüzden bir AĞ isteği DEĞİL, aynı dizinin istemci-taraflı
// bir görünümüdür (owner kararı — "the table defaults to result=all"; S32b tasarım spec'i §4.4
// "Default = Tümü [P]" ile AYNI sonuca varır).
//
// **Yoklama YOKTUR** (sözleşme bu uca `pollAfterSeconds` vermez). Tazelik yerine
// `SecuritySiteSession` yeni bir okutma/TC sonucu KEŞFEDİLDİĞİNDE bu sorguyu AÇIKÇA geçersiz
// kılar — ARTIK yalnız `Allowed` değil, HER yeni sonuçta (S32b defect D3: `anyAllowed` koşulu
// reddedilenleri sonsuza dek görünmez bırakırdı, rayın kalktığı bu sürümde bu daha da kritik).
//
// **`date` GÖNDERİLMEZ** — sunucu çağıranın kendi zaman diliminde (principal `timezone` claim'i,
// yoksa `Europe/Istanbul`) bugünü çözer (§29.5).
//
// **Sayım kuralı (normatif, §29.5) — burada uygulanır, ekranda DEĞİL.** Yalnız
// `result==='Allowed'` satırları bir fiziksel varlık iddiası taşır; `deniedCount` GÖRÜNEN satır
// sayısından DEĞİL, zarfın `filteredTotal - allowedCount`'ından türetilir — `Denied` satırları
// en yeni 200 ile SINIRLI olduğundan (§29.5) görünen satır sayısı gerçek ret sayısından DÜŞÜK
// olabilir; footer'daki "N reddedilen deneme" bu yüzden HER ZAMAN gerçek sayıyı okumalı, kesilmiş
// diziyi SAYMAMALI. `truncated` (`rows.length < filteredTotal`) ayrı bir uyarıyı besler — kesilen
// satırlar sıralamanın (`at` azalan) SONUNDA kalan EN ESKİ rettlerdir (§29.5 "ret ufku").

import {
  type ApiError,
  type PagedResult,
  type SecurityEntryRowDto,
  isApiError,
  securityApi,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { securityKeys } from './securityKeys';

export interface SecurityDayEntriesResult {
  /** Sunucudan gelen TÜM satırlar (Allowed + Denied), `at` azalan — panel bunun üstünde süzer. */
  rows: SecurityEntryRowDto[];
  /** Yoklama (evacuation) sayısı — YALNIZ `result==='Allowed'` satırları (§29.5 sayım kuralı). */
  allowedCount: number;
  /** GERÇEK ret sayısı — `filteredTotal - allowedCount`'tan (200'lük kesilmeden BAĞIMSIZ, normatif). */
  deniedCount: number;
  /** Güvenlik-aracılı satır sayısı (`PersonnelQr` + `TcLookup`) — YALNIZ Allowed satırlar (K8/§6). */
  securityMediatedCount: number;
  /** Kapı koduyla gelen — personelin KENDİ BEYANI — satır sayısı (`GateQr`) — YALNIZ Allowed. */
  selfAssertedCount: number;
  /** `value.length < filteredTotal` (§29.5) — kesilmiş bir yanıtta EN ESKİ rettler eksiktir. */
  truncated: boolean;
  isPending: boolean;
  /** `gate-result` aksiyonu grant'sız (`403 FORBIDDEN`) — K4/§8.4; gerçek ve beklenen bir durum. */
  forbidden: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export function useSecurityDayEntries(siteUuid: string | null): SecurityDayEntriesResult {
  const query = useQuery<PagedResult<SecurityEntryRowDto>, ApiError>({
    queryKey: securityKeys.entries(siteUuid ?? '', 'today', 'all'),
    queryFn: () => securityApi.listEntries({ siteUuid: siteUuid as string, result: 'all' }),
    enabled: siteUuid != null,
    retry: false,
    staleTime: 30_000,
  });

  const rows = query.data?.items ?? [];
  // `total` (süzgeç ÖNCESİ) bu görünümde OKUNMAZ — istek zaten `result: 'all'` olduğundan
  // `total === filteredTotal` (süzgeç bir NO-OP, dosya başı notu).
  const filteredTotal = query.data?.filteredTotal ?? 0;
  const allowedRows = rows.filter((row) => row.result === 'Allowed');
  const allowedCount = allowedRows.length;
  // Gerçek ret sayısı zarftan — GÖRÜNEN `Denied` satır sayısından DEĞİL (200'lük kesilme bu
  // ikisini ayrıştırabilir, dosya başı notu).
  const deniedCount = Math.max(0, filteredTotal - allowedCount);
  const selfAssertedCount = allowedRows.filter((row) => row.method === 'GateQr').length;
  const forbidden =
    isApiError(query.error) &&
    query.error.statusCode === 403 &&
    query.error.errorCode === 'FORBIDDEN';

  return {
    rows,
    allowedCount,
    deniedCount,
    securityMediatedCount: allowedCount - selfAssertedCount,
    selfAssertedCount,
    truncated: rows.length < filteredTotal,
    isPending: query.isPending,
    forbidden,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
